import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { StaffShell } from '../components/StaffShell';
import { getAdminUsers, getClinicians, createStaffAccount, assignClinician } from '../lib/api';

export default function Superadmin() {
  const [users, setUsers] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [tab, setTab] = useState('staff');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('clinician');
  const [creating, setCreating] = useState(false);

  const [assignPatient, setAssignPatient] = useState('');
  const [assignClin, setAssignClin] = useState('');

  const load = () => {
    getAdminUsers().then(setUsers).catch(() => {});
    getClinicians().then(setClinicians).catch(() => {});
  };
  useEffect(load, []);

  const patients = users.filter((u) => u.role === 'patient');
  const staff = users.filter((u) => ['clinician', 'admin', 'superadmin'].includes(u.role));

  const createStaff = async () => {
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a name and valid email');
      return;
    }
    setCreating(true);
    try {
      await createStaffAccount({ name: name.trim(), email: email.trim().toLowerCase(), role });
      toast.success(`${role} account created`);
      setName('');
      setEmail('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create account');
    } finally {
      setCreating(false);
    }
  };

  const doAssign = async () => {
    if (!assignPatient || !assignClin) {
      toast.error('Select a patient and clinician');
      return;
    }
    try {
      await assignClinician(assignPatient, assignClin);
      toast.success('Patient assigned');
      setAssignPatient('');
      setAssignClin('');
    } catch (e) {
      toast.error('Could not assign');
    }
  };

  return (
    <StaffShell title="Super Admin" badge="System management">
      <h1 className="font-display text-3xl text-white mb-6">Super admin</h1>

      <div className="flex gap-2 mb-5">
        {[{ k: 'staff', l: 'Staff accounts' }, { k: 'assign', l: 'Assignments' }].map((t) => (
          <button key={t.k} data-testid={`super-tab-${t.k}`} onClick={() => setTab(t.k)} className="rounded-full px-4 py-2 text-sm font-medium transition-base" style={tab === t.k ? { background: 'var(--aura-purple)', color: '#fff' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'staff' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="glass-dark rounded-2xl p-5">
            <h3 className="font-display text-lg text-white mb-3">Create staff account</h3>
            <input data-testid="staff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none placeholder-white/40" />
            <input data-testid="staff-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none placeholder-white/40" />
            <select data-testid="staff-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full mb-4 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
              <option value="clinician" className="text-black">Clinician</option>
              <option value="admin" className="text-black">Admin</option>
              <option value="superadmin" className="text-black">Super admin</option>
            </select>
            <button data-testid="create-staff-btn" onClick={createStaff} disabled={creating} className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-70">
              {creating ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-user-plus" /> Create account</>}
            </button>
          </div>

          <div className="glass-dark rounded-2xl overflow-hidden">
            <div className="px-4 py-3 text-xs uppercase tracking-wide text-white/40 border-b border-white/10">Existing staff ({staff.length})</div>
            {staff.map((u) => (
              <div key={u.id} className="px-4 py-3 border-b border-white/5 flex items-center justify-between text-sm">
                <div><div className="text-white font-medium">{u.name}</div><div className="text-white/50 text-xs">{u.email}</div></div>
                <span className="text-xs rounded-full px-2.5 py-1 bg-white/10 text-white/80 capitalize">{u.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'assign' && (
        <div className="glass-dark rounded-2xl p-5 max-w-xl">
          <h3 className="font-display text-lg text-white mb-3">Assign clinician to patient</h3>
          <label className="block text-xs text-white/50 mb-1">Patient</label>
          <select data-testid="assign-patient" value={assignPatient} onChange={(e) => setAssignPatient(e.target.value)} className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
            <option value="" className="text-black">Select patient…</option>
            {patients.map((p) => <option key={p.id} value={p.id} className="text-black">{p.name} — {p.email}</option>)}
          </select>
          <label className="block text-xs text-white/50 mb-1">Clinician</label>
          <select data-testid="assign-clinician" value={assignClin} onChange={(e) => setAssignClin(e.target.value)} className="w-full mb-4 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
            <option value="" className="text-black">Select clinician…</option>
            {clinicians.map((c) => <option key={c.id} value={c.id} className="text-black">{c.name} ({c.assigned_patients} assigned)</option>)}
          </select>
          <button data-testid="assign-btn" onClick={doAssign} className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2">
            <i className="fa-solid fa-link" /> Assign
          </button>
        </div>
      )}
    </StaffShell>
  );
}
